import { Link } from "react-router-dom";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { clinicInfo } from "@/mocks/site";

const careLinks = [
  { label: "All Services", to: "/services" },
  { label: "Spine Care", to: "/services/spine-care" },
  { label: "Orthopaedic Care", to: "/services/orthopaedic-care" },
];

const exploreLinks = [
  { label: "About Us", to: "/about" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
  { label: "Book Appointment", to: "/appointment" },
];

export default function Footer() {
  return (
    <footer className="bg-gradient-to-b from-primary-950 via-primary-950 to-primary-900 text-background-200">
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-col items-start justify-between gap-5 rounded-lg bg-gradient-to-br from-primary-600 to-accent-600 px-5 py-6 md:flex-row md:items-center md:px-8">
          <div>
            <h4 className="font-heading text-lg font-semibold text-background-50 md:text-xl">
              Health updates from Ortho Spine Care
            </h4>
            <p className="mt-1 font-body text-sm text-background-100">
              Gentle, practical guidance on bone, joint and spine health — no spam.
            </p>
          </div>
          <div className="w-full md:w-96">
            <NewsletterForm />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <img
                src={clinicInfo.logo}
                alt="Ortho Spine Care logo"
                className="h-11 w-auto rounded bg-background-50 px-2 py-1"
              />
            </div>
            <p className="mt-4 font-body text-sm leading-relaxed text-background-300">
              Ortho Spine Care is an orthopaedic and spine surgery practice in Srirangam, Trichy, led by
              Dr. N. Ajit Kumar, Senior Consultant in Orthopaedic and Spine Surgery.
            </p>
            <div className="mt-5">
              <Link
                to="/appointment"
                className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-gradient-to-br from-secondary-500 to-primary-500 px-4 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:from-secondary-600 hover:to-primary-600"
              >
                <i className="ri-calendar-check-line" aria-hidden="true"></i>
                Book Appointment
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-base font-semibold text-background-50">Services</h4>
            <ul className="mt-4 space-y-2.5">
              {careLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="font-body text-sm text-background-300 transition-colors hover:text-secondary-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base font-semibold text-background-50">Explore</h4>
            <ul className="mt-4 space-y-2.5">
              {exploreLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="font-body text-sm text-background-300 transition-colors hover:text-secondary-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base font-semibold text-background-50">Contact Us</h4>
            <ul className="mt-4 space-y-3 font-body text-sm text-background-300">
              <li className="flex items-start gap-2.5">
                <i className="ri-phone-fill mt-0.5 text-secondary-300" aria-hidden="true"></i>
                <a href={clinicInfo.phoneHref} className="hover:text-secondary-300">
                  {clinicInfo.phoneDisplay}
                </a>
              </li>
              {clinicInfo.email ? (
                <li className="flex items-start gap-2.5">
                  <i className="ri-mail-line mt-0.5 text-secondary-300" aria-hidden="true"></i>
                  <a href={`mailto:${clinicInfo.email}`} className="hover:text-secondary-300">
                    {clinicInfo.email}
                  </a>
                </li>
              ) : null}
              <li className="flex items-start gap-2.5">
                <i className="ri-whatsapp-line mt-0.5 text-secondary-300" aria-hidden="true"></i>
                <a
                  href={clinicInfo.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-secondary-300"
                  aria-label="Chat with us on WhatsApp"
                >
                  Chat on WhatsApp
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-map-pin-line mt-0.5 text-secondary-300" aria-hidden="true"></i>
                <span>{clinicInfo.addressFull}</span>
              </li>
              {clinicInfo.hours.length ? (
                <li className="flex items-start gap-2.5">
                  <i className="ri-time-line mt-0.5 text-secondary-300" aria-hidden="true"></i>
                  <span>
                    {clinicInfo.hours.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </span>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-primary-800/60">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
          <p className="font-body text-xs leading-relaxed text-background-400">
            <strong className="text-background-200">Medical disclaimer:</strong> The information on this website is
            provided for general health information only and does not replace consultation, diagnosis or treatment by a
            qualified healthcare professional. Always seek the advice of your physician regarding any medical condition.
          </p>
          <div className="mt-5 flex flex-col items-start justify-between gap-4 border-t border-primary-800/60 pt-5 sm:flex-row sm:items-center">
            <p className="font-body text-xs text-background-400">
              © {new Date().getFullYear()} Ortho Spine Care. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <Link to="/privacy-policy" className="font-body text-xs text-background-300 hover:text-secondary-300">
                Privacy Policy
              </Link>
              <Link to="/terms" className="font-body text-xs text-background-300 hover:text-secondary-300">
                Terms &amp; Conditions
              </Link>
              <Link to="/disclaimer" className="font-body text-xs text-background-300 hover:text-secondary-300">
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}